import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Menu,
  RotateCcw,
  Search,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useTaskTypeInstructions } from '../../hooks/useTaskTypeInstructions';
import { useViewModelReady } from '../../hooks/useViewModel';
import { TaskShell } from '../../components/TaskShell';
import { ConfirmDialog } from '../../components/TaskShell/ConfirmDialog';
import { SearchableDropdown } from '../../components/SearchableDropdown';
import { getTaskTypeDescriptor, listTaskTypeDescriptors } from '../../taskTypes/descriptor';
import { useTaskTypeRuntime } from '../../taskTypes/useTaskTypeRuntime';
import { TASK_STATUSES, TaskStatus } from '../../types';
import { BROWSABLE_TASK_STATUSES } from '../../services/tasks';
import { AdminHeader, AdminNavSheet } from '../AdminPage';
import { adminToolPath } from '../../components/AdminLayout';
import { readBrowseTasksSearchParams } from './filter';
import { ReviewAssignmentControl } from './ReviewAssignmentControl';
import { assignedReviewerLabel } from './reviewAssignment';
import { useBrowseTasksToolViewModel } from './viewModel';
import { paths } from '../../routes/paths';

export function BrowseTasksPage() {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const vm = useBrowseTasksToolViewModel();
  const { busy, dispose, initialize, run, setFilter } = vm;
  const [navOpen, setNavOpen] = useState(false);
  const [pendingAutoRunKey, setPendingAutoRunKey] = useState<string | null>(null);
  useViewModelReady(initialize);
  useEffect(() => () => dispose(), [dispose]);

  // All hooks must run on every render (rules-of-hooks) — keep them above
  // the admin-redirect so the hook order never changes.
  const modules = useMemo(
    () => listTaskTypeDescriptors(),
    [],
  );

  useEffect(() => {
    const routeState = readBrowseTasksSearchParams(new URLSearchParams(location.search));
    if (!routeState) return;
    setFilter(routeState.filter);
    if (routeState.autoRun) {
      setPendingAutoRunKey(location.search);
    }
  }, [location.search, setFilter]);

  useEffect(() => {
    if (!pendingAutoRunKey || busy) return;
    void run();
    setPendingAutoRunKey(null);
  }, [pendingAutoRunKey, busy, run]);

  if (!isAdmin) {
    return <Navigate to={paths.queue()} replace />;
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void run();
  };

  const mobileNavTrigger = isMobile ? (
    <button
      type="button"
      aria-label="Open admin tools"
      onClick={() => setNavOpen(true)}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900"
    >
      <Menu className="h-5 w-5" />
    </button>
  ) : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AdminHeader
        badge="Browse Tasks"
        backLabel="Back to admin"
        onBack={() => navigate(paths.admin())}
        compact={isMobile}
      />

      <main className="flex-1 px-4 py-5 sm:p-8">
        <section>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Browse Tasks</h1>
              <p className="text-sm text-gray-500 mt-1">
                Search tasks that have been claimed, are in progress, are in review, are awaiting
                rework, or have been approved. Search by task ID to find a task in any status.
                Step through them with previous / next.
              </p>
            </div>
            {mobileNavTrigger}
          </div>

          {vm.error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
              {vm.error}
            </div>
          )}

      {vm.busy ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <form
            onSubmit={onSubmit}
            className="mt-6 rounded-md border border-gray-200 bg-white p-4"
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
              <SearchableDropdown
                id="browse-campaign-filter"
                label="Campaign"
                options={[
                  { value: '', label: 'Any campaign' },
                  ...vm.campaigns.map((campaign) => ({
                    value: campaign.id,
                    label: campaign.name || '(no name)',
                    searchText: `${campaign.name} ${campaign.id}`,
                  })),
                ]}
                value={vm.filter.campaignId}
                onChange={(value) => vm.setFilter({ campaignId: value ?? '' })}
                placeholder="Any campaign"
                searchPlaceholder="Search campaigns"
                preserveOrder
              />

              <SearchableDropdown
                id="browse-task-type-filter"
                label="Task type"
                options={[
                  { value: '', label: 'Any task type' },
                  ...modules.map((module) => ({ value: module.id, label: module.displayName })),
                ]}
                value={vm.filter.taskTypeId}
                onChange={(value) => vm.setFilter({ taskTypeId: value ?? '' })}
                placeholder="Any task type"
                searchPlaceholder="Search task types"
                preserveOrder
              />

              <SearchableDropdown<TaskStatus | ''>
                id="browse-status-filter"
                label="Status"
                options={[
                  { value: '', label: 'Any status' },
                  ...BROWSABLE_TASK_STATUSES.map((status) => ({
                    value: status,
                    label: statusLabel(status),
                  })),
                ]}
                value={vm.filter.status}
                onChange={(value) => vm.setFilter({ status: value ?? '' })}
                placeholder="Any status"
                searchPlaceholder="Search statuses"
                preserveOrder
              />

              <FieldLabel label="Owner email">
                <input
                  type="email"
                  value={vm.filter.ownerEmail}
                  onChange={(e) => vm.setFilter({ ownerEmail: e.target.value, ownerUid: '' })}
                  placeholder="expert@example.com"
                  autoComplete="off"
                  className="block w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </FieldLabel>

              <FieldLabel label="Task ID (overrides other filters)">
                <input
                  type="text"
                  value={vm.filter.taskId}
                  onChange={(e) => vm.setFilter({ taskId: e.target.value })}
                  placeholder="e.g. abc123"
                  className="block w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </FieldLabel>
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="submit"
                disabled={vm.searching || !!vm.searchBlockedReason}
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {vm.searching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                {vm.searching ? 'Searching…' : 'Search'}
              </button>
              <button
                type="button"
                onClick={vm.resetFilter}
                disabled={vm.searching}
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
              {vm.searchBlockedReason && !vm.searchError && (
                <span className="text-xs text-gray-500">{vm.searchBlockedReason}</span>
              )}
              {vm.searchError && (
                <span className="text-xs text-red-600">{vm.searchError}</span>
              )}
            </div>
          </form>

          <ResultsSection vm={vm} />
        </>
      )}
        </section>
      </main>

      <AdminNavSheet
        open={navOpen}
        activeId="browseTasks"
        onClose={() => setNavOpen(false)}
        onSelect={(id) => {
          setNavOpen(false);
          navigate(adminToolPath(id));
        }}
        onBrowseTasks={() => setNavOpen(false)}
      />
    </div>
  );
}

function FieldLabel({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}

function ResultsSection({ vm }: { vm: ReturnType<typeof useBrowseTasksToolViewModel> }) {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const resultTask = vm.results[vm.selectedIndex] ?? null;
  const task = resultTask && vm.selectedTask?.id === resultTask.id
    ? vm.selectedTask
    : resultTask;
  const taskId = task?.id ?? null;
  const taskStatus = task?.status ?? null;
  const [targetStatus, setTargetStatus] = useState<TaskStatus>(task?.status ?? 'available');
  const descriptor = getTaskTypeDescriptor(task?.taskType);
  const runtime = useTaskTypeRuntime(task?.taskType ?? null);
  const instructionsState = useTaskTypeInstructions(task?.taskType ?? null);

  useEffect(() => {
    if (taskStatus) setTargetStatus(taskStatus);
  }, [taskId, taskStatus]);

  if (!vm.hasSearched) {
    return (
      <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
        Pick a campaign or owner (or paste a task ID) and click Search to browse tasks.
      </div>
    );
  }
  if (vm.results.length === 0) {
    return (
      <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
        No tasks match those filters.
      </div>
    );
  }

  const atFirst = vm.selectedIndex <= 0;
  const atLast = vm.selectedIndex >= vm.results.length - 1;
  const statusChanged = !!task && targetStatus !== task.status;

  // The AI-reviewer rerun button is only meaningful when the task type has a
  // server-side reviewer registered (the `hasLlmReviewer` advisory flag on
  // the module) and the task is in a non-terminal state. We hide the button
  // entirely otherwise — surfacing it for approved/archived tasks would
  // promise an action the callable refuses to perform.
  const canRerunReviewer =
    !!task &&
    descriptor.capabilities.llmReviewer &&
    (task.status === 'claimed' ||
      task.status === 'in_progress' ||
      task.status === 'in_review' ||
      task.status === 'changes_requested');

  const onConfirmDelete = async () => {
    await vm.tombstoneSelected();
    setConfirmDeleteOpen(false);
  };

  return (
    <>
      <div className="mt-6 flex flex-col gap-3 rounded-md border border-gray-200 bg-white px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:py-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-gray-600">
          <span className="font-medium text-gray-900">
            {vm.selectedIndex + 1} of {vm.results.length}
          </span>
          {task?.id && (
            <span className="font-mono text-xs text-gray-400">{task.id}</span>
          )}
          {task && (task.ownerName || task.expertId) && (
            <span className="truncate text-xs">
              <span className="text-gray-500">Owner: </span>
              <span className="text-gray-900">
                {task.ownerName || '(no name)'}
              </span>
              {task.expertId && (
                <span className="text-gray-500"> (Expert ID: {task.expertId})</span>
              )}
            </span>
          )}
          {task && (task.status === 'in_review' || task.reviewerId) && (
            <span className="truncate text-xs">
              <span className="text-gray-500">Reviewer: </span>
              <span className="text-gray-900">
                {assignedReviewerLabel(task, vm.users)}
              </span>
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={vm.prev}
            disabled={atFirst}
            className="inline-flex min-h-[36px] items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Previous
          </button>
          <button
            type="button"
            onClick={vm.next}
            disabled={atLast}
            className="inline-flex min-h-[36px] items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
          {task && (
            <ReviewAssignmentControl
              task={task}
              users={vm.users}
              assigning={vm.assigningReviewer}
              onAssign={vm.assignSelectedReview}
            />
          )}
          {task && (
            <div className="col-span-2 inline-flex min-h-[36px] items-stretch rounded-md border border-gray-300 bg-white sm:col-span-1">
              <SearchableDropdown<TaskStatus>
                id="browse-task-status"
                options={TASK_STATUSES.map((status) => ({
                  value: status,
                  label: statusLabel(status),
                }))}
                value={targetStatus}
                onChange={(value) => {
                  if (value !== null) setTargetStatus(value);
                }}
                disabled={vm.updatingStatus}
                ariaLabel="Task status"
                preserveOrder
                className="min-w-[11rem] flex-1"
                triggerClassName="min-h-[36px] rounded-r-none border-0 px-2 py-1.5 text-xs text-gray-700"
              />
              <button
                type="button"
                onClick={() => {
                  void vm.changeSelectedStatus(targetStatus);
                }}
                disabled={!statusChanged || vm.updatingStatus}
                title="Apply task status"
                className="inline-flex min-w-[4.75rem] items-center justify-center gap-1 rounded-r-md border-l border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {vm.updatingStatus ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
                Apply
              </button>
            </div>
          )}
          {canRerunReviewer && (
            <button
              type="button"
              onClick={() => {
                void vm.rerunReviewerForSelected();
              }}
              disabled={vm.rerunningReviewer}
              title="Run the AI reviewer on this task. Pending AI comments on the current review round are replaced; anything already accepted or rejected by a human reviewer is kept."
              className="col-span-2 inline-flex min-h-[36px] items-center justify-center gap-1 rounded-md border border-indigo-300 bg-white px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed sm:col-span-1"
            >
              {vm.rerunningReviewer ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              {vm.rerunningReviewer ? 'Running AI…' : 'Run AI reviewer'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={!task || vm.tombstoning}
            className="col-span-2 inline-flex min-h-[36px] items-center justify-center gap-1 rounded-md border border-red-300 bg-white px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed sm:col-span-1"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete task
          </button>
        </div>
      </div>

      {vm.reviewerRerunStatus?.kind === 'success' && (
        <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          AI reviewer finished. Added {vm.reviewerRerunStatus.commentsAdded}{' '}
          {vm.reviewerRerunStatus.commentsAdded === 1 ? 'comment' : 'comments'}
          {vm.reviewerRerunStatus.commentsCleared > 0 && (
            <>
              {' '}
              · cleared {vm.reviewerRerunStatus.commentsCleared} pending
            </>
          )}
          .
        </div>
      )}
      {vm.reviewerRerunStatus?.kind === 'report' && (
        <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          AI review report rebuilt. Open the task to see it.
        </div>
      )}
      {vm.reviewerRerunStatus?.kind === 'error' && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {vm.reviewerRerunStatus.message}
        </div>
      )}

      {vm.tombstoneError && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {vm.tombstoneError}
        </div>
      )}

      {vm.statusChangeError && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {vm.statusChangeError}
        </div>
      )}

      {vm.reviewerAssignmentError && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {vm.reviewerAssignmentError}
        </div>
      )}

      {task && runtime.status === 'loading' && (
        <div className="mt-2 flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-12 text-sm text-gray-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading task runtime…
        </div>
      )}
      {task && runtime.status === 'error' && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-medium">
            {runtime.staleDeployment
              ? 'This task runtime is from an older deployment.'
              : 'The task runtime could not be loaded.'}
          </p>
          {runtime.error && <p className="mt-1 text-xs text-red-700">{runtime.error.message}</p>}
          <button
            type="button"
            onClick={runtime.retry}
            className="mt-3 rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
          >
            Try again
          </button>
        </div>
      )}
      {task && runtime.module && runtime.status === 'ready' && (
        <div className="mt-2">
          <TaskShell
            mode="preview"
            module={runtime.module}
            instructions={instructionsState.instructions}
            task={task}
            result={task.result ?? runtime.module.emptyResult}
            onResultChange={() => {
              /* preview mode — inputs are disabled; onChange is never invoked */
            }}
            comments={vm.comments}
            isAdminViewer={true}
            fullBleed={true}
            embedded={true}
          />
        </div>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen && !!task}
        title="Delete this task?"
        message={
          task
            ? `This will archive "${task.title || task.id}" so it disappears from the queue and from this browser. The document is kept in Firestore with status "archived" — recoverable if needed, but the current owner${task.ownerName ? ` (${task.ownerName})` : ''} will lose access. Continue?`
            : ''
        }
        confirmLabel="Delete task"
        tone="danger"
        busy={vm.tombstoning}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          void onConfirmDelete();
        }}
      />
    </>
  );
}

function statusLabel(status: TaskStatus): string {
  switch (status) {
    case 'available':
      return 'Available';
    case 'claimed':
      return 'Claimed';
    case 'in_progress':
      return 'In progress';
    case 'in_review':
      return 'In review';
    case 'changes_requested':
      return 'Changes requested';
    case 'approved':
      return 'Approved';
    case 'archived':
      return 'Archived';
    default:
      return status;
  }
}
